import { api, apiUpload } from './api';

export enum RelationshipType {
  FATHER = 'FATHER',
  MOTHER = 'MOTHER',
  SIBLING = 'SIBLING',
  SPOUSE = 'SPOUSE',
  CHILD = 'CHILD',
  FATHER_IN_LAW = 'FATHER_IN_LAW',
  MOTHER_IN_LAW = 'MOTHER_IN_LAW',
  GRANDPARENT_IN_LAW = 'GRANDPARENT_IN_LAW',
  AUNT_IN_LAW = 'AUNT_IN_LAW',
  UNCLE_IN_LAW = 'UNCLE_IN_LAW',
  COUSIN_IN_LAW = 'COUSIN_IN_LAW',
}

export enum LineageType {
  BIOLOGICAL = 'BIOLOGICAL',
  ADOPTIVE = 'ADOPTIVE',
}

export type DocType = 'BIRTH_CERTIFICATE' | 'IC_PASSPORT';

export interface FamilyMember {
  id: string;
  userId: string;
  memberId: string;
  email: string | null;
  relationship: RelationshipType;
  lineageType: LineageType;
  targetSpouseId: string | null;
  firstName: string;
  lastName: string | null;
  gender: 'MALE' | 'FEMALE';
  dateOfBirth: string | null;
  isDeceased: boolean;
  dateOfDeath: string | null;
  passportNumber: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  birthCertificateUrl: string | null;
  nricPassportUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface FamilyTreeOwner {
  id: string;
  gender: 'MALE' | 'FEMALE' | null;
  firstName: string;
  lastName: string | null;
}

export interface FamilyTree {
  owner: FamilyTreeOwner;
  members: FamilyMember[];
}

export interface CreateFamilyMemberData {
  relationship: RelationshipType;
  lineageType?: LineageType;
  targetSpouseId?: string;
  firstName: string;
  lastName?: string;
  gender: 'MALE' | 'FEMALE';
  dateOfBirth?: string;
  isDeceased?: boolean;
  dateOfDeath?: string;
  passportNumber?: string;
  email?: string;
  address?: string;
  city?: string;
  state?: string;
  postalCode?: string;
}

export type UpdateFamilyMemberData = Partial<Omit<CreateFamilyMemberData, 'relationship' | 'targetSpouseId'>>;

export const IN_LAW_RELATIONSHIPS: RelationshipType[] = [
  RelationshipType.FATHER_IN_LAW,
  RelationshipType.MOTHER_IN_LAW,
  RelationshipType.GRANDPARENT_IN_LAW,
  RelationshipType.AUNT_IN_LAW,
  RelationshipType.UNCLE_IN_LAW,
  RelationshipType.COUSIN_IN_LAW,
];

export const DIRECT_RELATIONSHIPS: RelationshipType[] = [
  RelationshipType.FATHER,
  RelationshipType.MOTHER,
  RelationshipType.SIBLING,
  RelationshipType.SPOUSE,
  RelationshipType.CHILD,
];

/** Gender implied by the relationship word (locked); null = user chooses. */
export function impliedGender(rel: RelationshipType): 'MALE' | 'FEMALE' | null {
  switch (rel) {
    case RelationshipType.FATHER:
    case RelationshipType.FATHER_IN_LAW:
    case RelationshipType.UNCLE_IN_LAW:
      return 'MALE';
    case RelationshipType.MOTHER:
    case RelationshipType.MOTHER_IN_LAW:
    case RelationshipType.AUNT_IN_LAW:
      return 'FEMALE';
    default:
      return null;
  }
}

export function isInLaw(rel: RelationshipType): boolean {
  return IN_LAW_RELATIONSHIPS.includes(rel);
}

/** Returns spouses sorted by createdAt (1st wife, 2nd wife, ...). */
export function sortedSpouses(allMembers: FamilyMember[]): FamilyMember[] {
  return allMembers
    .filter((m) => m.relationship === RelationshipType.SPOUSE)
    .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
}

/** 1-based ordinal of a spouse among all spouses (by createdAt). null if not a spouse. */
export function spouseOrdinal(member: FamilyMember, allMembers: FamilyMember[]): number | null {
  if (member.relationship !== RelationshipType.SPOUSE) return null;
  const idx = sortedSpouses(allMembers).findIndex((m) => m.id === member.id);
  return idx >= 0 ? idx + 1 : null;
}

/** Map<spouseId, ordinal> for batch lookups. */
export function spouseOrdinalMap(allMembers: FamilyMember[]): Map<string, number> {
  const map = new Map<string, number>();
  sortedSpouses(allMembers).forEach((s, i) => map.set(s.id, i + 1));
  return map;
}

/** Set of biological in-law parent relationships already filled for a given spouse. */
export function filledInLawSlots(spouseId: string, allMembers: FamilyMember[]): Set<RelationshipType> {
  const filled = new Set<RelationshipType>();
  for (const m of allMembers) {
    if (
      m.targetSpouseId === spouseId &&
      m.lineageType === LineageType.BIOLOGICAL &&
      (m.relationship === RelationshipType.FATHER_IN_LAW ||
        m.relationship === RelationshipType.MOTHER_IN_LAW)
    ) {
      filled.add(m.relationship);
    }
  }
  return filled;
}

export const familyTreeService = {
  async getTree(): Promise<FamilyTree> {
    const response = await api.get<FamilyTree>('/family-tree');
    return response.data;
  },

  async addMember(data: CreateFamilyMemberData): Promise<FamilyMember> {
    const response = await api.post<FamilyMember>('/family-tree', data);
    return response.data;
  },

  async updateMember(id: string, data: UpdateFamilyMemberData): Promise<FamilyMember> {
    const response = await api.put<FamilyMember>(`/family-tree/${id}`, data);
    return response.data;
  },

  async deleteMember(id: string): Promise<void> {
    await api.delete(`/family-tree/${id}`);
  },

  async uploadDocument(id: string, file: File, docType: DocType): Promise<FamilyMember> {
    const formData = new FormData();
    formData.append('document', file);
    formData.append('docType', docType);
    const response = await apiUpload<FamilyMember>(`/family-tree/${id}/documents`, formData);
    return response.data;
  },

  async removeDocument(id: string, docType: DocType): Promise<FamilyMember> {
    const response = await api.delete<FamilyMember>(`/family-tree/${id}/documents/${docType}`);
    return response.data;
  },
};
